import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';

// Helper function to get model descriptions
const getModelDescription = (model) => {
  const descriptions = {
    'FlightLogEntry': 'Flight log data (times, fuel, engine hours)',
    'Sector': 'Flight sector information (departure/arrival)',
    'Trip': 'Trip/charter information',
    'Aircraft': 'Aircraft details (registration, model)',
    'User': 'User/crew information',
    'Maintenance': 'Maintenance work orders',
    'DutyRosterEntry': 'Crew duty roster entries',
    'InventoryItem': 'Parts and inventory',
    'Tool': 'Tools and equipment'
  };
  return descriptions[model] || '';
};

// Helper function to get preset data sources based on document type
const getPresetDataSources = (documentType) => {
  const presets = {
    'fdr': [
      { name: 'flightLogEntry', model: 'FlightLogEntry', lookupField: '_id', populate: [] },
      { name: 'sector', model: 'Sector', lookupField: '_id', populate: ['aircraftId', 'assignedPilots', 'tripId'] },
      { name: 'aircraft', model: 'Aircraft', lookupField: '_id', populate: [] }
    ],
    'itinerary': [
      { name: 'trip', model: 'Trip', lookupField: '_id', populate: [] },
      { name: 'sector', model: 'Sector', lookupField: '_id', populate: ['aircraftId', 'assignedPilots'] }
    ],
    'maintenance-report': [
      { name: 'maintenance', model: 'Maintenance', lookupField: '_id', populate: ['aircraftId', 'assignedTo'] },
      { name: 'aircraft', model: 'Aircraft', lookupField: '_id', populate: [] }
    ],
    'duty-roster': [
      { name: 'dutyRosterEntry', model: 'DutyRosterEntry', lookupField: '_id', populate: ['crew'] }
    ]
  };
  return presets[documentType] || [];
};

const FieldMappingForm = ({ open, onClose, onSuccess, existingMapping = null }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    mappingId: '',
    name: '',
    description: '',
    documentType: '',
    department: '',
    templateId: '',
    version: '1.0.0',
    isDefault: false,
    dataSources: [],
    mappings: {},
    sections: [],
    transformations: {},
    validationRules: {}
  });

  // UI state
  const [expandedAccordion, setExpandedAccordion] = useState('basic');
  const [editingMapping, setEditingMapping] = useState(null);
  const [simpleMode, setSimpleMode] = useState(true); // Simple mode by default
  const [newMappingKey, setNewMappingKey] = useState('');
  const [newMappingValue, setNewMappingValue] = useState('');
  const [newMappingSource, setNewMappingSource] = useState('');
  const [newMappingTransform, setNewMappingTransform] = useState('');
  const [newMappingDefault, setNewMappingDefault] = useState('');
  
  // Model schema state
  const [modelSchemas, setModelSchemas] = useState({});
  const [fieldSuggestions, setFieldSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const departments = [
    'OPERATIONS',
    'SALES',
    'MAINTENANCE',
    'SAFETY',
    'PLANNING',
    'ENGINEERING',
    'STORE',
    'GENERAL'
  ];

  const documentTypes = [
    'itinerary',
    'fdr',
    'maintenance-report',
    'duty-roster',
    'inspection-report',
    'other'
  ];

  const availableModels = [
    'FlightLogEntry',
    'Sector',
    'Trip',
    'Aircraft',
    'User',
    'Maintenance',
    'DutyRosterEntry',
    'InventoryItem',
    'Tool'
  ];

  // Common transformation templates
  const transformationTemplates = {
    'formatDateTime': 'new Date(value).toLocaleString()',
    'formatDate': 'new Date(value).toLocaleDateString()',
    'formatTime': 'new Date(value).toLocaleTimeString()',
    'formatCurrency': 'new Intl.NumberFormat(\'en-US\', { style: \'currency\', currency: \'USD\' }).format(value)',
    'formatHours': 'value ? value.toFixed(2) + \' hours\' : \'N/A\'',
    'formatNumber': 'value ? value.toFixed(2) : \'0\'',
    'uppercase': 'value ? value.toUpperCase() : \'\'',
    'lowercase': 'value ? value.toLowerCase() : \'\'',
    'capitalize': 'value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : \'\'',
    'mapCrewArray': 'Array.isArray(value) ? value.map(c => ({ name: c.firstName + \' \' + c.lastName, role: c.role })) : []',
    'defaultIfEmpty': 'value || \'N/A\'',
    'formatPhone': 'value ? value.replace(/(\\d{3})(\\d{3})(\\d{4})/, \'($1) $2-$3\') : \'\''
  };

  useEffect(() => {
    if (existingMapping) {
      setFormData({
        mappingId: existingMapping.mappingId || '',
        name: existingMapping.name || '',
        description: existingMapping.description || '',
        documentType: existingMapping.documentType || '',
        department: existingMapping.department || '',
        templateId: existingMapping.templateId || '',
        version: existingMapping.version || '1.0.0',
        isDefault: existingMapping.isDefault || false,
        dataSources: existingMapping.dataSources || [],
        mappings: existingMapping.mappings || {},
        sections: existingMapping.sections || [],
        transformations: existingMapping.transformations || {},
        validationRules: existingMapping.validationRules || {}
      });
    } else {
      resetForm();
    }
  }, [existingMapping, open]);

  // Fetch model schemas when data sources change
  useEffect(() => {
    formData.dataSources.forEach(source => {
      if (source.model && !modelSchemas[source.model]) {
        fetchModelSchema(source.model);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dataSources]);

  // Refresh field suggestions when data sources or document type changes
  useEffect(() => {
    if (formData.dataSources.length > 0 && formData.documentType) {
      const timer = setTimeout(() => {
        refreshFieldSuggestions();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dataSources, formData.documentType]);

  const resetForm = () => {
    setFormData({
      mappingId: '',
      name: '',
      description: '',
      documentType: '',
      department: '',
      templateId: '',
      version: '1.0.0',
      isDefault: false,
      dataSources: [],
      mappings: {},
      sections: [],
      transformations: {},
      validationRules: {}
    });
    setError(null);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-add preset data sources when document type is selected in simple mode
      if (field === 'documentType' && value && simpleMode && prev.dataSources.length === 0) {
        const presets = getPresetDataSources(value);
        if (presets.length > 0) {
          updated.dataSources = presets;
          showToast(`Added ${presets.length} recommended data sources`, 'success');
        }
      }
      
      return updated;
    });
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Data Sources Management
  const addDataSource = () => {
    const newSource = {
      name: '',
      model: '',
      lookupField: '_id',
      populate: []
    };
    setFormData(prev => ({
      ...prev,
      dataSources: [...prev.dataSources, newSource]
    }));
  };

  const updateDataSource = async (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.dataSources];
      updated[index] = { ...updated[index], [field]: value };
      
      // Auto-populate lookup field when model is selected
      if (field === 'model' && value) {
        fetchModelSchema(value).then(schema => {
          if (schema && schema.commonLookupFields && schema.commonLookupFields.length > 0) {
            updated[index].lookupField = schema.commonLookupFields[0];
            setFormData(prev => ({
              ...prev,
              dataSources: updated
            }));
          }
        });
      }
      
      return { ...prev, dataSources: updated };
    });
    
    // Refresh field suggestions when data sources change
    if (field === 'model' || field === 'name') {
      setTimeout(() => {
        refreshFieldSuggestions();
      }, 500);
    }
  };
  
  // Fetch model schema from API
  const fetchModelSchema = async (modelName) => {
    if (!modelName || modelSchemas[modelName]) {
      return modelSchemas[modelName];
    }
    
    try {
      const response = await axios.get(`/api/v1/model-schema/${modelName}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setModelSchemas(prev => ({
        ...prev,
        [modelName]: response.data
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error fetching model schema:', error);
      return null;
    }
  };
  
  // Refresh field suggestions based on current data sources
  const refreshFieldSuggestions = async () => {
    if (formData.dataSources.length === 0) {
      setFieldSuggestions([]);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      const response = await axios.post('/api/v1/pdf/field-mappings/suggestions', {
        dataSources: formData.dataSources,
        documentType: formData.documentType
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setFieldSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Error fetching field suggestions:', error);
      setFieldSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };
  
  // Auto-suggest sections based on field mappings
  const autoSuggestSections = () => {
    const sections = {};
    
    Object.keys(formData.mappings).forEach(templateVar => {
      const parts = templateVar.split('.');
      const sectionName = parts[0];
      
      if (!sections[sectionName]) {
        sections[sectionName] = {
          name: sectionName,
          label: sectionName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim(),
          order: Object.keys(sections).length,
          fields: []
        };
      }
      
      sections[sectionName].fields.push({
        templateVariable: templateVar,
        label: parts[parts.length - 1].replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()
      });
    });
    
    return Object.values(sections);
  };

  const removeDataSource = (index) => {
    setFormData(prev => ({
      ...prev,
      dataSources: prev.dataSources.filter((_, i) => i !== index)
    }));
  };

  // Field Mappings Management
  const addMapping = () => {
    if (!newMappingKey.trim()) {
      showToast('Please enter a template variable name', 'error');
      return;
    }

    let mappingValue;
    if (newMappingSource) {
      mappingValue = {
        source: newMappingSource,
        ...(newMappingTransform && { transform: newMappingTransform }),
        ...(newMappingDefault && { default: newMappingDefault })
      };
    } else {
      mappingValue = newMappingValue || '';
    }

    setFormData(prev => ({
      ...prev,
      mappings: {
        ...prev.mappings,
        [newMappingKey]: mappingValue
      }
    }));

    setNewMappingKey('');
    setNewMappingValue('');
    setNewMappingSource('');
    setNewMappingTransform('');
    setNewMappingDefault('');
  };

  const removeMapping = (key) => {
    setFormData(prev => {
      const updated = { ...prev.mappings };
      delete updated[key];
      return { ...prev, mappings: updated };
    });
  };

  const startEditMapping = (key, value) => {
    setEditingMapping(key);
    if (typeof value === 'object' && value.source) {
      setNewMappingSource(value.source);
      setNewMappingTransform(value.transform || '');
      setNewMappingDefault(value.default || '');
    } else {
      setNewMappingValue(value);
    }
    setNewMappingKey(key);
  };

  const saveMappingEdit = () => {
    if (!newMappingKey.trim()) {
      showToast('Please enter a template variable name', 'error');
      return;
    }

    let mappingValue;
    if (newMappingSource) {
      mappingValue = {
        source: newMappingSource,
        ...(newMappingTransform && { transform: newMappingTransform }),
        ...(newMappingDefault && { default: newMappingDefault })
      };
    } else {
      mappingValue = newMappingValue || '';
    }

    setFormData(prev => {
      const updated = { ...prev.mappings };
      if (editingMapping !== newMappingKey) {
        delete updated[editingMapping];
      }
      updated[newMappingKey] = mappingValue;
      return { ...prev, mappings: updated };
    });

    setEditingMapping(null);
    setNewMappingKey('');
    setNewMappingValue('');
    setNewMappingSource('');
    setNewMappingTransform('');
    setNewMappingDefault('');
  };

  const cancelMappingEdit = () => {
    setEditingMapping(null);
    setNewMappingKey('');
    setNewMappingValue('');
    setNewMappingSource('');
    setNewMappingTransform('');
    setNewMappingDefault('');
  };

  // Transformations Management
  const addTransformation = () => {
    const key = prompt('Enter transformation name:');
    if (!key) return;
    const value = prompt('Enter transformation function (JavaScript):');
    if (!value) return;

    setFormData(prev => ({
      ...prev,
      transformations: {
        ...prev.transformations,
        [key]: value
      }
    }));
  };

  const removeTransformation = (key) => {
    setFormData(prev => {
      const updated = { ...prev.transformations };
      delete updated[key];
      return { ...prev, transformations: updated };
    });
  };

  // Sections Management
  const addSection = () => {
    const newSection = {
      name: '',
      label: '',
      order: formData.sections.length,
      fields: []
    };
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const updateSection = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.sections];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, sections: updated };
    });
  };

  const removeSection = (index) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Validation
      if (!formData.mappingId || !formData.name || !formData.documentType || !formData.department) {
        throw new Error('Please fill in all required fields');
      }

      if (formData.dataSources.length === 0) {
        throw new Error('Please add at least one data source');
      }

      const payload = {
        ...formData,
        version: formData.version.startsWith('v') ? formData.version : `v${formData.version}`
      };

      // Remove _id from payload if it exists (for update)
      delete payload._id;

      let response;
      const isEdit = existingMapping && existingMapping._id;

      if (isEdit) {
        // Update existing field mapping
        const version = existingMapping.version || formData.version;
        const versionStr = version.startsWith('v') ? version : `v${version}`;
        response = await axios.put(
          `/api/v1/pdf/field-mappings/${formData.mappingId}/${versionStr}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        showToast('Field mapping updated successfully', 'success');
      } else {
        // Create new field mapping
        response = await axios.post('/api/v1/pdf/field-mappings', payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        showToast('Field mapping created successfully', 'success');
      }

      if (onSuccess) onSuccess(response.data.fieldMapping);
      handleClose();

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || (existingMapping && existingMapping._id ? 'Failed to update field mapping' : 'Failed to create field mapping');
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {existingMapping ? 'Edit Field Mapping' : 'Create Field Mapping Configuration'}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={simpleMode}
                  onChange={(e) => setSimpleMode(e.target.checked)}
                  size="small"
                />
              }
              label="Simple Mode"
            />
            <Tooltip title="Field mappings define how database fields map to template variables">
              <HelpIcon color="action" />
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Basic Information */}
          <Accordion expanded={expandedAccordion === 'basic'} onChange={handleAccordionChange('basic')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Basic Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Mapping ID"
                    value={formData.mappingId}
                    onChange={handleChange('mappingId')}
                    required
                    helperText="Unique identifier for this mapping (e.g., FDR-field-mapping)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.name}
                    onChange={handleChange('name')}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={handleChange('description')}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Document Type</InputLabel>
                    <Select
                      value={formData.documentType}
                      onChange={handleChange('documentType')}
                      label="Document Type"
                    >
                      {documentTypes.map(type => (
                        <MenuItem key={type} value={type}>
                          {type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={formData.department}
                      onChange={handleChange('department')}
                      label="Department"
                    >
                      {departments.map(dept => (
                        <MenuItem key={dept} value={dept}>
                          {dept.replace(/_/g, ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Version"
                    value={formData.version}
                    onChange={handleChange('version')}
                    helperText="Semantic version (e.g., 1.0.0)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Template ID (Optional)"
                    value={formData.templateId}
                    onChange={handleChange('templateId')}
                    helperText="Link to a specific template"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isDefault}
                        onChange={handleChange('isDefault')}
                      />
                    }
                    label="Set as default mapping for this document type"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Data Sources */}
          <Accordion expanded={expandedAccordion === 'sources'} onChange={handleAccordionChange('sources')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Data Sources ({formData.dataSources.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Helpful explanation */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>What are Data Sources?</strong>
                </Typography>
                <Typography variant="body2">
                  Data sources tell the system which database tables (models) to fetch information from when generating your PDF.
                  For example, if you're creating an FDR (Flight Data Record), you might need data from:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                  <li><strong>FlightLogEntry</strong> - for flight times, fuel, engine hours</li>
                  <li><strong>Sector</strong> - for departure/arrival airports, dates</li>
                  <li><strong>Aircraft</strong> - for aircraft registration, model</li>
                  <li><strong>User</strong> - for crew/pilot information</li>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Tip:</strong> Add one data source for each type of information you need in your PDF.
                </Typography>
              </Alert>

              {/* Preset templates based on document type */}
              {formData.documentType && formData.dataSources.length === 0 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Quick Start - Common Data Sources for {formData.documentType.toUpperCase().replace(/-/g, ' ')}:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Click on any chip below to automatically add the recommended data sources:
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    {getPresetDataSources(formData.documentType).map((preset, idx) => (
                      <Chip
                        key={idx}
                        label={`+ ${preset.name} (${preset.model})`}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            dataSources: [...prev.dataSources, preset]
                          }));
                        }}
                        color="primary"
                        variant="outlined"
                        sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Stack spacing={2}>
                {formData.dataSources.map((source, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Data Source #{index + 1}
                      </Typography>
                      <Tooltip title="Remove this data source">
                        <IconButton
                          color="error"
                          onClick={() => removeDataSource(index)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Source Name"
                          value={source.name}
                          onChange={(e) => updateDataSource(index, 'name', e.target.value)}
                          placeholder="e.g., flightLogEntry"
                          helperText="A name to reference this data"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Database Table (Model)</InputLabel>
                          <Select
                            value={source.model}
                            onChange={(e) => updateDataSource(index, 'model', e.target.value)}
                            label="Database Table (Model)"
                          >
                            {availableModels.map(model => (
                              <MenuItem key={model} value={model}>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {model}
                                  </Typography>
                                  {getModelDescription(model) && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {getModelDescription(model)}
                                    </Typography>
                                  )}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Which database table to fetch data from
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Lookup Field</InputLabel>
                          <Select
                            value={source.lookupField || '_id'}
                            onChange={(e) => updateDataSource(index, 'lookupField', e.target.value)}
                            label="Lookup Field"
                          >
                            {source.model && modelSchemas[source.model] && modelSchemas[source.model].commonLookupFields && Array.isArray(modelSchemas[source.model].commonLookupFields) ? (
                              modelSchemas[source.model].commonLookupFields.map(field => (
                                <MenuItem key={field} value={field}>
                                  {field}
                                  {field === '_id' && ' (Default)'}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value="_id">_id (Default)</MenuItem>
                            )}
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Field to search by (auto-suggested based on model)
                        </Typography>
                      </Grid>
                    </Grid>
                    {source.model && (
                      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                        <Typography variant="caption" display="block">
                          <strong>Example:</strong> This will fetch data from the <strong>{source.model}</strong> table
                          {source.name && ` and you can reference it as "${source.name}" in your field mappings`}.
                          {getModelDescription(source.model) && (
                            <span> ({getModelDescription(source.model)})</span>
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={addDataSource}
                  variant="outlined"
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Data Source Manually
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Field Mappings */}
          <Accordion expanded={expandedAccordion === 'mappings'} onChange={handleAccordionChange('mappings')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Field Mappings ({Object.keys(formData.mappings).length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {/* Field Suggestions */}
                {fieldSuggestions.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Suggested Field Mappings</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Click on any suggestion below to automatically add it:
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      {fieldSuggestions.slice(0, 10).map((suggestion, idx) => (
                        <Chip
                          key={idx}
                          label={`${suggestion.templateVariable} ← ${suggestion.sourcePath.split('.').pop()}`}
                          onClick={() => {
                            setNewMappingKey(suggestion.templateVariable);
                            setNewMappingSource(suggestion.sourcePath);
                            showToast(`Added: ${suggestion.templateVariable}`, 'info');
                          }}
                          color="primary"
                          variant="outlined"
                          sx={{ cursor: 'pointer' }}
                          size="small"
                        />
                      ))}
                    </Stack>
                    {fieldSuggestions.length > 10 && (
                      <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        + {fieldSuggestions.length - 10} more suggestions available
                      </Typography>
                    )}
                  </Alert>
                )}
                
                {/* Add/Edit Mapping */}
                <Paper variant="outlined" sx={{ p: 2, bgcolor: editingMapping ? 'info.light' : 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {editingMapping ? `Edit Mapping: ${editingMapping}` : 'Add Field Mapping'}
                  </Typography>
                  {editingMapping && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Editing existing mapping. Changes will be saved when you click "Save" below.
                    </Alert>
                  )}
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Template Variable</InputLabel>
                        <Select
                          value={newMappingKey}
                          onChange={(e) => setNewMappingKey(e.target.value)}
                          label="Template Variable"
                          displayEmpty
                          disabled={!!editingMapping}
                        >
                          <MenuItem value="">
                            <em>Or type manually</em>
                          </MenuItem>
                          {fieldSuggestions.map((suggestion, idx) => (
                            <MenuItem key={idx} value={suggestion.templateVariable}>
                              {suggestion.templateVariable}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                        label="Or Type Manually"
                        value={newMappingKey}
                        onChange={(e) => setNewMappingKey(e.target.value)}
                        placeholder="e.g., flightNumber"
                        disabled={!!editingMapping}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Source Path (for data from database)</InputLabel>
                        <Select
                          value={newMappingSource}
                          onChange={(e) => {
                            setNewMappingSource(e.target.value);
                            // Clear static value when source is selected
                            if (e.target.value) {
                              setNewMappingValue('');
                            }
                          }}
                          label="Source Path (for data from database)"
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Or type manually</em>
                          </MenuItem>
                          {fieldSuggestions
                            .filter(s => !newMappingKey || s.templateVariable === newMappingKey)
                            .map((suggestion, idx) => (
                              <MenuItem key={idx} value={suggestion.sourcePath}>
                                {suggestion.sourcePath}
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  ({suggestion.description})
                                </Typography>
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                        label="Or Type Manually (Source Path)"
                        value={newMappingSource}
                        onChange={(e) => {
                          setNewMappingSource(e.target.value);
                          // Clear static value when source is entered
                          if (e.target.value) {
                            setNewMappingValue('');
                          }
                        }}
                        placeholder="e.g., sector.tripId.charterNumber"
                        helperText="Use dot notation for nested fields"
                      />
                      <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                        label="Or Static Value (literal string/URL)"
                        value={newMappingValue}
                        onChange={(e) => {
                          setNewMappingValue(e.target.value);
                          // Clear source when static value is entered
                          if (e.target.value) {
                            setNewMappingSource('');
                          }
                        }}
                        placeholder="e.g., www.goldeneagle.aero or https://example.com/image.png"
                        helperText="For static values (not from database). Leave empty if using Source Path above."
                        disabled={!!newMappingSource}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Transform (optional)</InputLabel>
                        <Select
                          value={newMappingTransform}
                          onChange={(e) => {
                            setNewMappingTransform(e.target.value);
                            // Auto-add transformation if not exists
                            if (e.target.value && !formData.transformations[e.target.value]) {
                              const template = transformationTemplates[e.target.value];
                              if (template) {
                                setFormData(prev => ({
                                  ...prev,
                                  transformations: {
                                    ...prev.transformations,
                                    [e.target.value]: template
                                  }
                                }));
                              }
                            }
                          }}
                          label="Transform (optional)"
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {Object.keys(transformationTemplates).map(key => (
                            <MenuItem key={key} value={key}>
                              {key}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      {editingMapping ? (
                        <Stack spacing={1}>
                          <Button
                            startIcon={<SaveIcon />}
                            onClick={saveMappingEdit}
                            variant="contained"
                            size="small"
                            fullWidth
                            color="success"
                          >
                            Save
                          </Button>
                          <Button
                            startIcon={<CancelIcon />}
                            onClick={cancelMappingEdit}
                            variant="outlined"
                            size="small"
                            fullWidth
                          >
                            Cancel
                          </Button>
                        </Stack>
                      ) : (
                        <Button
                          startIcon={<AddIcon />}
                          onClick={addMapping}
                          variant="contained"
                          size="small"
                          fullWidth
                        >
                          Add
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Paper>

                {/* Existing Mappings */}
                {Object.keys(formData.mappings).length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Template Variable</TableCell>
                          <TableCell>Source/Value</TableCell>
                          <TableCell>Transform</TableCell>
                          <TableCell>Default</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(formData.mappings).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell>
                              <Chip label={key} size="small" />
                            </TableCell>
                            <TableCell>
                              {typeof value === 'object' && value.source ? (
                                <Typography variant="body2" color="primary">
                                  {value.source}
                                </Typography>
                              ) : (
                                <Typography variant="body2">{String(value)}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {typeof value === 'object' && value.transform && (
                                <Chip label={value.transform} size="small" color="secondary" />
                              )}
                            </TableCell>
                            <TableCell>
                              {typeof value === 'object' && value.default !== undefined && (
                                <Typography variant="body2">{String(value.default)}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => startEditMapping(key, value)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeMapping(key)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Transformations - Hidden in simple mode */}
          {!simpleMode && (
            <Accordion expanded={expandedAccordion === 'transformations'} onChange={handleAccordionChange('transformations')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Transformations ({Object.keys(formData.transformations).length}) - Advanced
                </Typography>
              </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Transformations format your data. Click on a template below to add it, or create your own.
                </Typography>
              </Alert>
              
              {/* Transformation Templates */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Common Transformation Templates:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(transformationTemplates).map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`+ ${key}`}
                      onClick={() => {
                        if (!formData.transformations[key]) {
                          setFormData(prev => ({
                            ...prev,
                            transformations: {
                              ...prev.transformations,
                              [key]: value
                            }
                          }));
                          showToast(`Added transformation: ${key}`, 'success');
                        } else {
                          showToast(`Transformation ${key} already exists`, 'info');
                        }
                      }}
                      color={formData.transformations[key] ? 'default' : 'primary'}
                      variant={formData.transformations[key] ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
              
              <Stack spacing={2}>
                {Object.entries(formData.transformations).map(([key, value]) => (
                  <Paper key={key} variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">{key}</Typography>
                        <Typography variant="body2" color="text.secondary" component="code" sx={{ 
                          display: 'block', 
                          mt: 0.5,
                          p: 1,
                          bgcolor: 'grey.100',
                          borderRadius: 1,
                          fontFamily: 'monospace'
                        }}>
                          {value}
                        </Typography>
                        {transformationTemplates[key] && (
                          <Chip label="Template" size="small" color="primary" sx={{ mt: 1 }} />
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeTransformation(key)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const key = prompt('Enter transformation name:');
                    if (!key) return;
                    const value = prompt('Enter transformation function (JavaScript):');
                    if (!value) return;

                    setFormData(prev => ({
                      ...prev,
                      transformations: {
                        ...prev.transformations,
                        [key]: value
                      }
                    }));
                  }}
                  variant="outlined"
                  size="small"
                >
                  Add Custom Transformation
                </Button>
              </Stack>
            </AccordionDetails>
            </Accordion>
          )}

          {/* Sections - Hidden in simple mode */}
          {!simpleMode && (
            <Accordion expanded={expandedAccordion === 'sections'} onChange={handleAccordionChange('sections')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Sections ({formData.sections.length}) - Advanced
                </Typography>
              </AccordionSummary>
            <AccordionDetails>
              {/* Auto-suggest sections button */}
              {Object.keys(formData.mappings).length > 0 && formData.sections.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Auto-Generate Sections</strong>
                      </Typography>
                      <Typography variant="body2">
                        We can automatically organize your field mappings into sections based on their names.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        const suggestedSections = autoSuggestSections();
                        setFormData(prev => ({
                          ...prev,
                          sections: suggestedSections
                        }));
                        showToast(`Auto-generated ${suggestedSections.length} sections`, 'success');
                      }}
                    >
                      Auto-Generate Sections
                    </Button>
                  </Box>
                </Alert>
              )}
              
              <Stack spacing={2}>
                {formData.sections.map((section, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Section #{index + 1}
                      </Typography>
                      <IconButton
                        color="error"
                        onClick={() => removeSection(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Section Name"
                          value={section.name}
                          onChange={(e) => updateSection(index, 'name', e.target.value)}
                          helperText="Internal name (e.g., flightInfo)"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Section Label"
                          value={section.label}
                          onChange={(e) => updateSection(index, 'label', e.target.value)}
                          helperText="Display label (e.g., Flight Information)"
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Order"
                          value={section.order}
                          onChange={(e) => updateSection(index, 'order', parseInt(e.target.value))}
                          helperText="Display order (lower = first)"
                        />
                      </Grid>
                    </Grid>
                    {section.fields && section.fields.length > 0 && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="caption" fontWeight="bold">
                          Fields in this section:
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                          {section.fields.map((field, idx) => (
                            <Chip
                              key={idx}
                              label={field.templateVariable}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Paper>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={addSection}
                  variant="outlined"
                  size="small"
                >
                  Add Section Manually
                </Button>
              </Stack>
            </AccordionDetails>
            </Accordion>
          )}

          {/* Simple Mode Help */}
          {simpleMode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Simple Mode:</strong> Advanced options (Transformations, Sections) are hidden. 
                Toggle "Simple Mode" off to access these features.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={<SaveIcon />}
        >
          {submitting ? 'Saving...' : 'Save Field Mapping'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FieldMappingForm;

